(function () {
  const ROOT_SELECTOR = '[data-braided-bracelet-options]:not([data-braided-options-not-ready])';
  const NOT_READY_SELECTOR = '[data-braided-options-not-ready]';
  const SPLIT_PATTERN = /\s+\+\s+/;

  class BraidedBraceletOptions {
    constructor(root) {
      if (!root || root.dataset.braidedInitialized === 'true') return;

      this.root = root;
      this.root.dataset.braidedInitialized = 'true';
      this.sectionId = root.dataset.sectionId;
      this.productFormId = root.dataset.productFormId;
      this.noAddonValue = root.dataset.noAddonValue || '不加購';
      this.product = this.readProductJson();
      this.form = this.productFormId ? document.getElementById(this.productFormId) : null;
      this.productForm = this.form?.closest('product-form') || null;

      this.syncCheckboxesFromNative();
      this.syncSelectedState({ dispatchNativeChange: false });
      this.bindEvents();
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-braided-addon-none], [data-braided-addon-checkbox]')) {
          this.handleAddonCheckboxChange(event.target);
          return;
        }

        if (event.target.closest('[data-braided-pendant-native]')) {
          this.syncSelectedState({ dispatchNativeChange: false });
          this.clearError();
        }

        if (event.target.matches('[data-braided-required]')) {
          this.clearError();
        }
      });

      if (this.form) {
        this.form.addEventListener(
          'submit',
          (event) => {
            this.validateBeforeSubmit(event);
          },
          true
        );
      }
    }

    readProductJson() {
      const jsonNode = this.root.querySelector('[data-braided-product-json]');

      if (!jsonNode?.textContent?.trim()) return null;

      try {
        return JSON.parse(jsonNode.textContent);
      } catch (error) {
        console.warn('Unable to parse braided bracelet product JSON.', error);
        return null;
      }
    }

    get noAddonCheckbox() {
      return this.root.querySelector('[data-braided-addon-none]');
    }

    get addonCheckboxes() {
      return Array.from(this.root.querySelectorAll('[data-braided-addon-checkbox]'));
    }

    get addonPropertyInput() {
      return this.root.querySelector('[data-braided-addon-property]');
    }

    get addonError() {
      return this.root.querySelector('[data-braided-addon-error]');
    }

    get selectedPendantInput() {
      return this.root.querySelector('[data-braided-pendant-native] input[type="radio"]:checked');
    }

    get selectedAddonNativeInput() {
      return this.root.querySelector('[data-braided-addon-native] input[type="radio"]:checked');
    }

    get requiredPropertyFields() {
      return Array.from(this.root.querySelectorAll('[data-braided-required]'));
    }

    get selectedPendantValue() {
      return this.selectedPendantInput?.value || '';
    }

    get selectedAddonNativeValue() {
      return this.selectedAddonNativeInput?.value || '';
    }

    handleAddonCheckboxChange(changedInput) {
      const noAddonCheckbox = this.noAddonCheckbox;
      const addonCheckboxes = this.addonCheckboxes;

      if (!noAddonCheckbox) return;

      if (changedInput === noAddonCheckbox && changedInput.checked) {
        addonCheckboxes.forEach((checkbox) => {
          checkbox.checked = false;
        });
      }

      if (changedInput !== noAddonCheckbox && changedInput.checked) {
        noAddonCheckbox.checked = false;
      }

      if (!addonCheckboxes.some((checkbox) => checkbox.checked)) {
        noAddonCheckbox.checked = true;
      }

      this.syncSelectedState({ dispatchNativeChange: true });
      this.clearError();
    }

    syncCheckboxesFromNative() {
      const selectedNativeValue = this.selectedAddonNativeValue || this.noAddonValue;
      const selectedAddonValues =
        selectedNativeValue === this.noAddonValue ? [] : selectedNativeValue.split(SPLIT_PATTERN).map((value) => value.trim());

      if (this.noAddonCheckbox) {
        this.noAddonCheckbox.checked = selectedAddonValues.length === 0;
      }

      this.addonCheckboxes.forEach((checkbox) => {
        checkbox.checked = selectedAddonValues.includes(checkbox.value);
      });

      this.updateAddonProperty(selectedNativeValue);
    }

    getCheckboxComboValue() {
      const selectedAddonValues = this.addonCheckboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);

      if (!selectedAddonValues.length) return this.noAddonValue;

      return selectedAddonValues.join(' + ');
    }

    syncSelectedState({ dispatchNativeChange }) {
      const comboValue = this.getCheckboxComboValue();
      this.updateAddonProperty(comboValue);
      this.selectNativeAddonCombo(comboValue, dispatchNativeChange);
      this.updateVariantInput(comboValue);
    }

    updateAddonProperty(comboValue) {
      if (this.addonPropertyInput) {
        this.addonPropertyInput.value = comboValue;
      }
    }

    selectNativeAddonCombo(comboValue, dispatchNativeChange) {
      const nativeInput = Array.from(this.root.querySelectorAll('[data-braided-addon-native] input[type="radio"]')).find(
        (input) => input.value === comboValue
      );

      if (!nativeInput) {
        this.root.dataset.braidedVariantValid = 'false';
        this.showAddonError(`找不到「${comboValue}」對應的商品規格，請重新選擇加購。`);
        return false;
      }

      this.root.dataset.braidedVariantValid = 'true';

      if (!nativeInput.checked) {
        nativeInput.checked = true;
      }

      if (dispatchNativeChange) {
        nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return true;
    }

    updateVariantInput(comboValue) {
      const variant = this.findVariant(this.selectedPendantValue, comboValue);
      const variantInput = this.form?.querySelector('input[name="id"]');

      if (!variantInput) return variant;

      if (!variant) {
        variantInput.value = '';
        this.root.dataset.braidedVariantValid = 'false';
        return null;
      }

      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.root.dataset.braidedVariantValid = 'true';

      return variant;
    }

    findVariant(pendantValue, comboValue) {
      if (!this.product?.variants?.length || !pendantValue || !comboValue) return null;

      return (
        this.product.variants.find((variant) => {
          const option1 = this.getVariantOption(variant, 0);
          const option2 = this.getVariantOption(variant, 1);

          return option1 === pendantValue && option2 === comboValue;
        }) || null
      );
    }

    getVariantOption(variant, index) {
      if (Array.isArray(variant.options)) {
        return variant.options[index];
      }

      return variant[`option${index + 1}`];
    }

    validateBeforeSubmit(event) {
      const comboValue = this.getCheckboxComboValue();
      const variant = this.findVariant(this.selectedPendantValue, comboValue);
      const missingProperty = this.requiredPropertyFields.find((field) => !field.value);
      const errors = [];

      if (!this.selectedPendantValue) {
        errors.push('請先選擇吊墜款式。');
      }

      if (!variant) {
        errors.push('目前的吊墜與加購組合沒有對應的商品規格，請重新選擇。');
      }

      if (missingProperty) {
        const label = this.root.querySelector(`label[for="${missingProperty.id}"]`)?.textContent?.trim() || '必填欄位';
        errors.push(`請先選擇${label}。`);
      }

      if (!errors.length) {
        this.clearError();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      this.showError(errors[0]);

      if (missingProperty) {
        missingProperty.focus();
      } else if (!this.selectedPendantValue) {
        this.root.querySelector('[data-braided-pendant-native] input[type="radio"]')?.focus();
      }
    }

    showAddonError(message) {
      if (!this.addonError) return;

      this.addonError.textContent = message;
      this.addonError.hidden = false;
    }

    showError(message) {
      this.showAddonError(message);

      const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const errorMessage = this.productForm?.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.hidden = false;
      }
    }

    clearError() {
      if (this.addonError) {
        this.addonError.textContent = '';
        this.addonError.hidden = true;
      }

      const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      const errorMessage = this.productForm?.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorMessage.textContent = '';
        errorWrapper.hidden = true;
      }
    }
  }

  function initBraidedBraceletOptions(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      new BraidedBraceletOptions(root);
    });

    scope.querySelectorAll(NOT_READY_SELECTOR).forEach((root) => {
      disableNotReadyProductForm(root);
    });

    removeRedMarkedDescriptionParagraphs(scope);
  }

  function disableNotReadyProductForm(root) {
    if (!root || root.dataset.braidedNotReadyHandled === 'true') return;

    root.dataset.braidedNotReadyHandled = 'true';

    const form = root.dataset.productFormId ? document.getElementById(root.dataset.productFormId) : null;
    const submitButton = form?.querySelector('[name="add"]');
    const variantInput = form?.querySelector('input[name="id"]');

    if (variantInput) {
      variantInput.disabled = true;
      variantInput.value = '';
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.querySelector('span').textContent = '商品規格設定中';
    }
  }

  function removeRedMarkedDescriptionParagraphs(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const productInfo = root.closest('product-info') || document;

      productInfo.querySelectorAll('.product__description p').forEach((paragraph) => {
        const text = paragraph.textContent || '';

        if (text.includes('不影響價格') || text.includes('併入本商品規格')) {
          paragraph.remove();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initBraidedBraceletOptions());
  } else {
    initBraidedBraceletOptions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initBraidedBraceletOptions(event.target);
  });
})();
